import React, { useState, useEffect } from 'react';
import DocumentStepBuilder from './document_step_builder';

const StepBuilder = ({ step, index, onChange, onDelete, getAvailableQuestionnaires, errors = {} }) => {
  const [isExpanded, setIsExpanded] = useState(!step.id || step.id.toString().startsWith('new_'));
  const [questionnaires, setQuestionnaires] = useState([]);
  
  useEffect(() => {
    const loadQuestionnaires = async () => {
      const availableQuestionnaires = await getAvailableQuestionnaires();
      setQuestionnaires(availableQuestionnaires);
    };
    
    loadQuestionnaires();
  }, [getAvailableQuestionnaires]);

  const handleFieldChange = (field, value) => {
    const updatedStep = {
      ...step,
      [field]: value
    };
    
    // Clear questionnaire when changing from questionnaire to document upload
    if (field === 'step_type' && value !== 'questionnaire') {
      updatedStep.questionnaire_id = null;
    }
    
    onChange(updatedStep);
  };

  const handleDelete = () => {
    swal({
      title: 'Delete this step?',
      text: 'This action cannot be undone.',
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    }).then(result => {
      if (result.value) {
        onDelete();
      }
    });
  };

  const getStepSummary = () => {
    if (!step.title) return 'New Step';
    
    let summary = step.title;
    if (step.step_type === 'questionnaire' && step.questionnaire_id) {
      const questionnaire = questionnaires.find(q => q.id === step.questionnaire_id);
      if (questionnaire) {
        summary += ` (${questionnaire.name})`;
      }
    } else if (step.step_type === 'document_upload') {
      summary += ' (Document Upload)';
    }
    
    return summary;
  };

  return (
    <>
      <div className={`step-builder ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div
        className="step-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="step-summary">
          <span className="step-number">#{index + 1}</span>
          <span className="step-text">
            {getStepSummary()}
          </span>
          <span className="step-type badge badge-secondary ml-2">
            {step.step_type?.replace('_', ' ').toUpperCase() || 'QUESTIONNAIRE'}
          </span>
          {step.step_type !== 'questionnaire' && step.requirement_type === 'required' && (
            <span className="badge badge-danger ml-1">Required</span>
          )}
          {step.step_type !== 'questionnaire' && step.requirement_type === 'optional' && (
            <span className="badge badge-warning ml-1">Optional</span>
          )}
          {step.signature_required && (
            <span className="badge badge-dark ml-1">Signature</span>
          )}
        </div>
        <div className="step-actions">
          <button
            type="button"
            className="btn btn-link btn-sm text-danger"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
            title="Delete Step"
          >
            <i className="fa fa-trash"></i>
          </button>
          <i className={`fa ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} ml-2`}></i>
        </div>
      </div>

      {isExpanded && (
        <div className="step-content">
            <div className="row">
              <div className="col-md-8">
                <div className="form-group">
                  <label className="form-label">Step Title</label>
                  <input
                    type="text"
                    className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                    value={step.title || ''}
                    onChange={(e) => handleFieldChange('title', e.target.value)}
                    placeholder="Enter step title..."
                  />
                  {errors.title && (
                    <div className="invalid-feedback">{errors.title}</div>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label className="form-label">Step Type</label>
                  <select
                    className="form-control"
                    value={step.step_type || 'document_upload'}
                    onChange={(e) => handleFieldChange('step_type', e.target.value)}
                  >
                    <option value="document_upload">Document Upload</option>
                    <option value="questionnaire">Questionnaire</option>
                  </select>
                </div>
              </div>
            </div>

            {step.step_type !== 'questionnaire' && (
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={step.description || ''}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Enter step description..."
                />
              </div>
            )}

            {step.step_type !== 'questionnaire' && (
              <div className="form-group">
                <label className="form-label">Instructions</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={step.instructions || ''}
                  onChange={(e) => handleFieldChange('instructions', e.target.value)}
                  placeholder="Enter instructions for users completing this step..."
                />
              </div>
            )}

            {step.step_type === 'questionnaire' ? (
              <div className="form-group">
                <label className="form-label">Select Questionnaire</label>
                <select
                  className={`form-control ${errors.questionnaire_id ? 'is-invalid' : ''}`}
                  value={step.questionnaire_id || ''}
                  onChange={(e) => handleFieldChange('questionnaire_id', e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">Choose a questionnaire...</option>
                  {questionnaires.map(questionnaire => (
                    <option key={questionnaire.id} value={questionnaire.id}>
                      {questionnaire.name}
                      {questionnaire.description && ` - ${questionnaire.description.slice(0, 50)}${questionnaire.description.length > 50 ? '...' : ''}`}
                    </option>
                  ))}
                </select>
                {errors.questionnaire_id && (
                  <div className="invalid-feedback">{errors.questionnaire_id}</div>
                )}
                {questionnaires.length === 0 && (
                  <small className="form-text text-warning">
                    <i className="fa fa-exclamation-triangle mr-1"></i>
                    No questionnaires available. Please create a questionnaire first.
                  </small>
                )}
              </div>
            ) : (
              <DocumentStepBuilder
                step={step}
                onChange={onChange}
                errors={errors}
              />
            )}

            <div className="row">
              {step.step_type !== 'questionnaire' && (
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">Requirement Type</label>
                    <select
                      className="form-control"
                      value={step.requirement_type || 'required'}
                      onChange={(e) => handleFieldChange('requirement_type', e.target.value)}
                    >
                      <option value="required">Required</option>
                      <option value="optional">Optional</option>
                    </select>
                  </div>
                </div>
              )}
              {step.step_type !== 'questionnaire' && (
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">Signature</label>
                    <div className="form-check mt-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`signature_${step.id || index}`}
                        checked={step.signature_required || false}
                        onChange={(e) => handleFieldChange('signature_required', e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor={`signature_${step.id || index}`}>
                        Require electronic signature
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {step.step_type !== 'questionnaire' && step.signature_required && (
              <div className="form-group">
                <label className="form-label">Signature Agreement Text</label>
                <textarea
                  className="form-control"
                  rows="4"
                  value={step.signature_agreement_text || ''}
                  onChange={(e) => handleFieldChange('signature_agreement_text', e.target.value)}
                  placeholder="Enter the text that users will agree to when signing (e.g., 'I certify that the information provided is accurate and complete...')"
                />
                <small className="form-text text-muted">
                  This text will be displayed to users when they are required to provide an electronic signature.
                </small>
              </div>
            )}
          </div>
        )}
      </div>
      
      <style>{`
        .step-builder {
          background: white;
        }
        
        .step-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          cursor: pointer;
          border-bottom: 1px solid transparent;
          transition: all 0.2s ease;
        }
        
        .step-header:hover {
          background-color: #f8f9fa;
        }
        
        .expanded .step-header {
          border-bottom-color: #dee2e6;
        }
        
        .step-summary {
          display: flex;
          align-items: center;
          flex: 1;
        }
        
        .step-number {
          font-weight: bold;
          color: #6c757d;
          margin-right: 0.5rem;
        }
        
        .step-text {
          flex: 1;
          margin-right: 0.5rem;
          font-weight: 500;
        }
        
        .step-actions {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .step-content {
          padding: 1rem;
          border-top: 1px solid #dee2e6;
        }
        
        .badge-sm {
          font-size: 0.75em;
        }
      `}</style>
    </>
  );
};

export default StepBuilder;